/** App chrome: top navigation, level indicator, account menu. */

import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { levelColor } from '../api'
import { useAuth } from '../store/auth'
import MobileTabBar from './MobileTabBar'
import { NAV_ITEMS } from './nav-items'
import { Avatar } from './ui'

export default function Layout() {
  const { user, signOut } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  // The map wants the whole viewport; other pages scroll normally.
  const isMap = location.pathname.startsWith('/map')

  return (
    <div className="flex h-full flex-col">
      <header className="pt-safe z-30 flex shrink-0 items-center gap-3 border-b border-ink-800 bg-ink-900/90 px-3 py-2 backdrop-blur">
        <Link to="/map" className="-m-1 flex items-center gap-2 p-1">
          <span className="jp grid h-9 w-9 place-items-center rounded-xl bg-brand-500/20 text-lg font-bold text-brand-400">
            漢
          </span>
          <span className="hidden text-sm font-bold tracking-tight text-slate-100 sm:block">
            Nihongo Visualizer
          </span>
        </Link>

        {/* Below lg these destinations live in the bottom tab bar instead, where
            they are within thumb reach and wide enough to hit. */}
        <nav aria-label="Main" className="ml-2 hidden flex-1 items-center gap-0.5 lg:flex">
          {NAV_ITEMS.filter((item) => user || !item.auth).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                  isActive ? 'bg-ink-800 text-white' : 'text-ink-500 hover:bg-ink-850 hover:text-slate-200'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Keeps the account control hard right once the nav above is hidden. */}
        <div className="flex-1 lg:hidden" />

        {user ? (
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex min-h-[44px] items-center gap-2 rounded-xl border border-ink-700 bg-ink-850 py-1 pl-1 pr-2 hover:border-ink-600"
            >
              <Avatar name={user.display_name} seed={user.avatar_seed} size={30} />
              <span className="hidden text-xs font-semibold text-slate-200 sm:block">{user.display_name}</span>
              <span
                className="rounded-full px-1.5 py-px text-[9px] font-bold"
                style={{ background: `${levelColor[user.target_level]}22`, color: levelColor[user.target_level] }}
              >
                {user.target_level}
              </span>
            </button>
            {menuOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close menu"
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setMenuOpen(false)}
                />
                <div role="menu" className="card absolute right-0 z-20 mt-1.5 w-52 overflow-hidden p-1">
                  <Link
                    to="/profile"
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-[44px] items-center rounded-lg px-3 text-sm text-slate-200 hover:bg-ink-800"
                  >
                    Profile &amp; settings
                  </Link>
                  <Link
                    to="/scores"
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-[44px] items-center rounded-lg px-3 text-sm text-slate-200 hover:bg-ink-800"
                  >
                    My scorecard
                  </Link>
                  {/* The tab bar drops /board to keep five tappable tabs, so it is
                      reachable here instead. */}
                  <Link
                    to="/board"
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-[44px] items-center rounded-lg px-3 text-sm text-slate-200 hover:bg-ink-800"
                  >
                    Scoreboard
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false)
                      signOut()
                    }}
                    className="flex min-h-[44px] w-full items-center rounded-lg px-3 text-left text-sm text-rose-300 hover:bg-rose-500/10"
                  >
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <Link to="/login" className="btn-primary text-xs">
            Sign in
          </Link>
        )}
      </header>

      <main className={isMap ? 'min-h-0 flex-1' : 'flex-1 overflow-y-auto'}>
        <Outlet />
      </main>

      <MobileTabBar signedIn={Boolean(user)} />
    </div>
  )
}
